import { useMemo } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'

// Types
interface Evaluator {
  id: string
  name: string
  avatar?: string
}

interface SignalDimension {
  id: string
  name: string
  status: 'aligned' | 'conflicted' | 'missing'
  level: 'strong' | 'moderate' | 'weak' | null
  evaluators: Evaluator[]
  summary?: string
}

interface CandidateDecision {
  id: string
  name: string
  avatar?: string
  recommendation: 'hire' | 'no-hire' | 'undecided'
  confidenceLevel: 'high' | 'medium' | 'low'
  levelFit: string
  signalStrengths: string[]
  signalConcerns: string[]
  evaluatorCount: number
  evaluatorAlignment: number // 0-100
  lastActivity: string
}

interface RoleData {
  id: string
  title: string
  levelRange: string
  locations: string[]
  decisionStatus: 'ready' | 'blocked' | 'pending'
  decisionReason: string
  candidatesCount: number
  evaluatorsActive: number
  dimensions: SignalDimension[]
  candidates: CandidateDecision[]
  decisionOwner?: Evaluator
  createdAt: string
}

// Mock data
const MOCK_ROLE: RoleData = {
  id: '1',
  title: 'Backend Engineer',
  levelRange: 'Senior–Principal',
  locations: ['San Francisco', 'Remote'],
  decisionStatus: 'ready',
  decisionReason: 'All evaluators aligned on recommendation with high confidence',
  candidatesCount: 5,
  evaluatorsActive: 4,
  decisionOwner: { id: '1', name: 'Sarah K.' },
  createdAt: '3 weeks ago',
  dimensions: [
    {
      id: '1',
      name: 'Systems Design',
      status: 'aligned',
      level: 'strong',
      evaluators: [
        { id: '1', name: 'Sarah K.' },
        { id: '2', name: 'Mike T.' },
        { id: '3', name: 'Alex R.' },
      ],
      summary: 'Demonstrated depth in distributed systems. Strong trade-off reasoning.',
    },
    {
      id: '2',
      name: 'Technical Communication',
      status: 'aligned',
      level: 'strong',
      evaluators: [
        { id: '1', name: 'Sarah K.' },
        { id: '2', name: 'Mike T.' },
      ],
      summary: 'Clear explanations. Adapted to audience level effectively.',
    },
    {
      id: '3',
      name: 'Problem Solving',
      status: 'conflicted',
      level: null,
      evaluators: [
        { id: '1', name: 'Sarah K.' },
        { id: '4', name: 'Jordan P.' },
      ],
      summary: 'Disagreement on problem decomposition approach. Sarah saw structured thinking; Jordan noted reliance on hints.',
    },
    {
      id: '4',
      name: 'Leadership & Mentorship',
      status: 'missing',
      level: null,
      evaluators: [],
      summary: 'No structured feedback collected for this dimension.',
    },
  ],
  candidates: [
    {
      id: '1',
      name: 'Chen Wei',
      recommendation: 'hire',
      confidenceLevel: 'high',
      levelFit: 'Senior',
      signalStrengths: ['Systems design depth', 'Clear communication', 'Strong debugging'],
      signalConcerns: [],
      evaluatorCount: 4,
      evaluatorAlignment: 100,
      lastActivity: '2 hours ago',
    },
    {
      id: '2',
      name: 'Priya Sharma',
      recommendation: 'hire',
      confidenceLevel: 'medium',
      levelFit: 'Senior',
      signalStrengths: ['Leadership experience', 'Broad technical range'],
      signalConcerns: ['Depth in distributed systems unclear'],
      evaluatorCount: 3,
      evaluatorAlignment: 67,
      lastActivity: '1 day ago',
    },
    {
      id: '3',
      name: 'Marcus Johnson',
      recommendation: 'undecided',
      confidenceLevel: 'low',
      levelFit: 'Mid–Senior',
      signalStrengths: ['Strong problem solving', 'Good cultural fit'],
      signalConcerns: ['Level calibration unclear', 'Missing systems design signal'],
      evaluatorCount: 2,
      evaluatorAlignment: 50,
      lastActivity: '3 days ago',
    },
    {
      id: '4',
      name: 'Emma Thompson',
      recommendation: 'no-hire',
      confidenceLevel: 'high',
      levelFit: 'Below bar',
      signalStrengths: ['Enthusiasm and growth mindset'],
      signalConcerns: ['Technical depth insufficient', 'Communication gaps'],
      evaluatorCount: 4,
      evaluatorAlignment: 100,
      lastActivity: '5 days ago',
    },
  ],
}

// Icons
function ArrowLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
    </svg>
  )
}

function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  )
}

function ExclamationCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
    </svg>
  )
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  )
}

function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
    </svg>
  )
}

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
    </svg>
  )
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
    </svg>
  )
}

function BoltIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
    </svg>
  )
}

function ExclamationTriangleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
    </svg>
  )
}

function QuestionMarkCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
    </svg>
  )
}

function HandThumbUpIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6.633 10.5c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 012.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 00.322-1.672V3a.75.75 0 01.75-.75A2.25 2.25 0 0116.5 4.5c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 01-2.649 7.521c-.388.482-.987.729-1.605.729H13.48c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 00-1.423-.23H5.904M14.25 9h2.25M5.904 18.75c.083.205.173.405.27.602.197.4-.078.898-.523.898h-.908c-.889 0-1.713-.518-1.972-1.368a12 12 0 01-.521-3.507c0-1.553.295-3.036.831-4.398C3.387 10.203 4.167 9.75 5 9.75h1.053c.472 0 .745.556.5.96a8.958 8.958 0 00-1.302 4.665c0 1.194.232 2.333.654 3.375z" />
    </svg>
  )
}

function HandThumbDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 15h2.25m8.024-9.75c.011.05.028.1.052.148.591 1.2.924 2.55.924 3.977a8.96 8.96 0 01-.999 4.125m.023-8.25c-.076-.365.183-.75.575-.75h.908c.889 0 1.713.518 1.972 1.368.339 1.11.521 2.287.521 3.507 0 1.553-.295 3.036-.831 4.398-.306.774-1.086 1.227-1.918 1.227h-1.053c-.472 0-.745-.556-.5-.96a8.95 8.95 0 00.303-.54m.023-8.25H16.48a4.5 4.5 0 01-1.423-.23l-3.114-1.04a4.5 4.5 0 00-1.423-.23H6.504c-.618 0-1.217.247-1.605.729A11.95 11.95 0 002.25 12c0 .434.023.863.068 1.285C2.427 14.306 3.346 15 4.372 15h3.126c.618 0 .991.724.725 1.282A7.471 7.471 0 007.5 19.5a2.25 2.25 0 002.25 2.25.75.75 0 00.75-.75v-.633c0-.573.11-1.14.322-1.672.304-.76.93-1.33 1.653-1.715a9.04 9.04 0 002.86-2.4c.498-.634 1.226-1.08 2.032-1.08h.384" />
    </svg>
  )
}

function ScaleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v17.25m0 0c-1.472 0-2.882.265-4.185.75M12 20.25c1.472 0 2.882.265 4.185.75M18.75 4.97A48.416 48.416 0 0012 4.5c-2.291 0-4.545.16-6.75.47m13.5 0c1.01.143 2.01.317 3 .52m-3-.52l2.62 10.726c.122.499-.106 1.028-.589 1.202a5.988 5.988 0 01-2.031.352 5.988 5.988 0 01-2.031-.352c-.483-.174-.711-.703-.59-1.202L18.75 4.971zm-16.5.52c.99-.203 1.99-.377 3-.52m0 0l2.62 10.726c.122.499-.106 1.028-.589 1.202a5.989 5.989 0 01-2.031.352 5.989 5.989 0 01-2.031-.352c-.483-.174-.711-.703-.59-1.202L5.25 4.971z" />
    </svg>
  )
}

function UserPlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zM4 19.235v-.11a6.375 6.375 0 0112.75 0v.109A12.318 12.318 0 0110.374 21c-2.331 0-4.512-.645-6.374-1.766z" />
    </svg>
  )
}

// Decision Readiness Banner
function DecisionReadinessBanner({ role }: { role: RoleData }) {
  const statusConfig = {
    ready: {
      bg: 'from-emerald-600 via-emerald-500 to-teal-500',
      icon: CheckCircleIcon,
      label: 'Ready to decide',
      sublabel: 'All signals collected. Evaluators aligned.',
    },
    blocked: {
      bg: 'from-amber-600 via-amber-500 to-orange-500',
      icon: ExclamationTriangleIcon,
      label: 'Decision blocked',
      sublabel: 'Conflicting signals need resolution.',
    },
    pending: {
      bg: 'from-surface-600 via-surface-500 to-surface-400',
      icon: ClockIcon,
      label: 'Awaiting signal',
      sublabel: 'More evaluations needed before deciding.',
    },
  }

  const config = statusConfig[role.decisionStatus]
  const StatusIcon = config.icon

  return (
    <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-r ${config.bg} text-white`}>
      {/* Subtle pattern overlay */}
      <div className="absolute inset-0 opacity-10">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="decision-pattern" width="32" height="32" patternUnits="userSpaceOnUse">
              <circle cx="16" cy="16" r="1.5" fill="white" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#decision-pattern)" />
        </svg>
      </div>

      <div className="relative px-6 py-8 sm:px-8 sm:py-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-white/20 backdrop-blur-sm">
              <StatusIcon className="w-8 h-8" />
            </div>
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h2 className="text-2xl sm:text-3xl font-display font-semibold">{config.label}</h2>
              </div>
              <p className="text-white/80 text-lg">{config.sublabel}</p>
              <p className="text-white/60 text-sm mt-2">{role.decisionReason}</p>
            </div>
          </div>

          {role.decisionStatus === 'ready' && (
            <button className="shrink-0 inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white text-emerald-700 font-semibold hover:bg-white/90 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5">
              <ScaleIcon className="w-5 h-5" />
              Finalize Decision
            </button>
          )}

          {role.decisionStatus === 'blocked' && (
            <button className="shrink-0 inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white text-amber-700 font-semibold hover:bg-white/90 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5">
              <ExclamationCircleIcon className="w-5 h-5" />
              Resolve Conflicts
            </button>
          )}

          {role.decisionStatus === 'pending' && (
            <button className="shrink-0 inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white text-surface-700 font-semibold hover:bg-white/90 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5">
              <UserPlusIcon className="w-5 h-5" />
              Invite Evaluator
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// Signal dimension card
function SignalDimensionCard({ dimension }: { dimension: SignalDimension }) {
  const statusConfig = {
    aligned: {
      border: 'border-emerald-200',
      bg: 'bg-emerald-50',
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
      icon: CheckCircleIcon,
      label: 'Aligned',
      labelColor: 'text-emerald-700',
    },
    conflicted: {
      border: 'border-amber-200',
      bg: 'bg-amber-50',
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      icon: ExclamationTriangleIcon,
      label: 'Conflicted',
      labelColor: 'text-amber-700',
    },
    missing: {
      border: 'border-surface-200',
      bg: 'bg-surface-50',
      iconBg: 'bg-surface-100',
      iconColor: 'text-surface-500',
      icon: QuestionMarkCircleIcon,
      label: 'Missing',
      labelColor: 'text-surface-600',
    },
  }

  const levelConfig = {
    strong: { label: 'Strong signal', color: 'text-emerald-600', bg: 'bg-emerald-100' },
    moderate: { label: 'Moderate signal', color: 'text-amber-600', bg: 'bg-amber-100' },
    weak: { label: 'Weak signal', color: 'text-rose-600', bg: 'bg-rose-100' },
  }

  const config = statusConfig[dimension.status]
  const StatusIcon = config.icon

  return (
    <div className={`p-5 rounded-xl border ${config.border} ${config.bg} transition-all hover:shadow-md`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${config.iconBg}`}>
            <StatusIcon className={`w-5 h-5 ${config.iconColor}`} />
          </div>
          <div>
            <h4 className="font-semibold text-surface-900">{dimension.name}</h4>
            <span className={`text-xs font-medium ${config.labelColor}`}>{config.label}</span>
          </div>
        </div>
        {dimension.level && (
          <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${levelConfig[dimension.level].bg} ${levelConfig[dimension.level].color}`}>
            {levelConfig[dimension.level].label}
          </span>
        )}
      </div>

      {dimension.summary && (
        <p className="text-sm text-surface-600 mb-3 leading-relaxed">{dimension.summary}</p>
      )}

      {dimension.evaluators.length > 0 && (
        <div className="flex items-center gap-2">
          <div className="flex -space-x-2">
            {dimension.evaluators.slice(0, 4).map((evaluator) => (
              <div
                key={evaluator.id}
                className="w-7 h-7 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center text-xs font-medium border-2 border-white"
                title={evaluator.name}
              >
                {evaluator.name.charAt(0)}
              </div>
            ))}
          </div>
          <span className="text-xs text-surface-500">
            {dimension.evaluators.length} evaluator{dimension.evaluators.length !== 1 ? 's' : ''}
          </span>
        </div>
      )}

      {dimension.evaluators.length === 0 && dimension.status === 'missing' && (
        <button className="inline-flex items-center gap-1.5 text-sm text-brand-600 hover:text-brand-700 font-medium">
          <UserPlusIcon className="w-4 h-4" />
          Request evaluation
        </button>
      )}
    </div>
  )
}

// Candidate decision card
function CandidateDecisionCard({ candidate }: { candidate: CandidateDecision }) {
  const recommendationConfig = {
    hire: {
      icon: HandThumbUpIcon,
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
      label: 'Hire',
      labelBg: 'bg-emerald-100',
      labelColor: 'text-emerald-700',
    },
    'no-hire': {
      icon: HandThumbDownIcon,
      bg: 'bg-rose-50',
      border: 'border-rose-200',
      iconBg: 'bg-rose-100',
      iconColor: 'text-rose-600',
      label: 'No Hire',
      labelBg: 'bg-rose-100',
      labelColor: 'text-rose-700',
    },
    undecided: {
      icon: ScaleIcon,
      bg: 'bg-surface-50',
      border: 'border-surface-200',
      iconBg: 'bg-surface-100',
      iconColor: 'text-surface-500',
      label: 'Undecided',
      labelBg: 'bg-surface-100',
      labelColor: 'text-surface-600',
    },
  }

  const confidenceConfig = {
    high: { label: 'High confidence', color: 'text-emerald-600' },
    medium: { label: 'Medium confidence', color: 'text-amber-600' },
    low: { label: 'Low confidence', color: 'text-rose-600' },
  }

  const config = recommendationConfig[candidate.recommendation]
  const RecommendationIcon = config.icon

  return (
    <Link
      to={`/candidates/${candidate.id}`}
      className={`block p-5 rounded-xl border ${config.border} ${config.bg} transition-all hover:shadow-lg hover:-translate-y-0.5 group`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-100 to-brand-200 text-brand-700 flex items-center justify-center text-lg font-semibold">
            {candidate.name.charAt(0)}
          </div>
          <div>
            <h4 className="font-semibold text-surface-900 group-hover:text-brand-700 transition-colors">
              {candidate.name}
            </h4>
            <p className="text-sm text-surface-500">Level: {candidate.levelFit}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className={`p-2 rounded-lg ${config.iconBg}`}>
            <RecommendationIcon className={`w-5 h-5 ${config.iconColor}`} />
          </div>
          <span className={`text-sm font-semibold px-3 py-1 rounded-full ${config.labelBg} ${config.labelColor}`}>
            {config.label}
          </span>
        </div>
      </div>

      {/* Alignment bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-sm mb-1.5">
          <span className="text-surface-600">Evaluator alignment</span>
          <span className={`font-medium ${confidenceConfig[candidate.confidenceLevel].color}`}>
            {candidate.evaluatorAlignment}% · {confidenceConfig[candidate.confidenceLevel].label}
          </span>
        </div>
        <div className="h-2 bg-surface-200 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${
              candidate.evaluatorAlignment >= 80
                ? 'bg-emerald-500'
                : candidate.evaluatorAlignment >= 50
                ? 'bg-amber-500'
                : 'bg-rose-500'
            }`}
            style={{ width: `${candidate.evaluatorAlignment}%` }}
          />
        </div>
        <p className="text-xs text-surface-500 mt-1">
          {candidate.evaluatorCount} evaluator{candidate.evaluatorCount !== 1 ? 's' : ''} · Last activity {candidate.lastActivity}
        </p>
      </div>

      {/* Strengths & Concerns */}
      <div className="grid sm:grid-cols-2 gap-4">
        {candidate.signalStrengths.length > 0 && (
          <div>
            <h5 className="text-xs font-semibold text-surface-500 uppercase tracking-wider mb-2">Strengths</h5>
            <ul className="space-y-1">
              {candidate.signalStrengths.map((strength, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-surface-700">
                  <span className="text-emerald-500 mt-0.5">+</span>
                  {strength}
                </li>
              ))}
            </ul>
          </div>
        )}
        {candidate.signalConcerns.length > 0 && (
          <div>
            <h5 className="text-xs font-semibold text-surface-500 uppercase tracking-wider mb-2">Concerns</h5>
            <ul className="space-y-1">
              {candidate.signalConcerns.map((concern, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-surface-700">
                  <span className="text-rose-500 mt-0.5">−</span>
                  {concern}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="mt-4 pt-3 border-t border-surface-200/50 flex items-center justify-end">
        <span className="text-sm font-medium text-brand-600 group-hover:text-brand-700 flex items-center gap-1">
          View details
          <ChevronRightIcon className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
        </span>
      </div>
    </Link>
  )
}

// Main page component
export default function RoleDecisionPage() {
  useParams<{ roleId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()

  // In production, fetch role data based on roleId
  const role = MOCK_ROLE

  // Group dimensions by status for signal overview
  const signalGroups = useMemo(() => {
    const aligned = role.dimensions.filter((d) => d.status === 'aligned')
    const conflicted = role.dimensions.filter((d) => d.status === 'conflicted')
    const missing = role.dimensions.filter((d) => d.status === 'missing')
    return { aligned, conflicted, missing }
  }, [role.dimensions])

  // Sort candidates by recommendation priority
  const sortedCandidates = useMemo(() => {
    const order = { hire: 0, undecided: 1, 'no-hire': 2 }
    return [...role.candidates].sort((a, b) => order[a.recommendation] - order[b.recommendation])
  }, [role.candidates])

  return (
    <div className="min-h-screen bg-surface-50">
      {/* Header */}
      <header className="bg-white border-b border-surface-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Back + Logo */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 -ml-2 rounded-lg text-surface-500 hover:text-surface-900 hover:bg-surface-100 transition-colors"
              >
                <ArrowLeftIcon className="w-5 h-5" />
              </button>
              <Link to="/" className="flex items-center gap-2">
                <img src="/images/logo.png" alt="JumpstoneHR" className="w-7 h-7" />
                <span className="font-semibold text-lg text-brand-600 hidden sm:block">Jumpstone</span>
              </Link>
            </div>

            {/* Role title */}
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <h1 className="font-semibold text-surface-900">{role.title}</h1>
                <p className="text-xs text-surface-500">{role.levelRange}</p>
              </div>
            </div>

            {/* User */}
            <div className="w-8 h-8 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center font-medium text-sm">
              {user?.email?.charAt(0).toUpperCase() || 'U'}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Role summary card */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-6 bg-white rounded-2xl border border-surface-200">
          <div>
            <h1 className="text-2xl font-display font-semibold text-surface-900 mb-1">{role.title}</h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-surface-600">
              <span className="font-medium text-brand-600">{role.levelRange}</span>
              <span className="flex items-center gap-1.5">
                <MapPinIcon className="w-4 h-4 text-surface-400" />
                {role.locations.join(' · ')}
              </span>
              <span className="flex items-center gap-1.5">
                <UsersIcon className="w-4 h-4 text-surface-400" />
                {role.candidatesCount} candidates · {role.evaluatorsActive} evaluators active
              </span>
            </div>
          </div>
          {role.decisionOwner && (
            <div className="flex items-center gap-2 text-sm text-surface-600">
              <span className="text-surface-400">Decision owner:</span>
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center text-xs font-medium">
                  {role.decisionOwner.name.charAt(0)}
                </div>
                <span className="font-medium">{role.decisionOwner.name}</span>
              </div>
            </div>
          )}
        </div>

        {/* Decision Readiness Banner */}
        <DecisionReadinessBanner role={role} />

        {/* Signal Overview */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-surface-900">Signal Overview</h2>
            <span className="text-sm text-surface-500">
              {signalGroups.aligned.length} aligned · {signalGroups.conflicted.length} conflicted · {signalGroups.missing.length} missing
            </span>
          </div>

          <div className="space-y-6">
            {/* Where evaluators agree */}
            {signalGroups.aligned.length > 0 && (
              <div>
                <h3 className="flex items-center gap-2 text-sm font-semibold text-emerald-700 mb-3">
                  <CheckCircleIcon className="w-4 h-4" />
                  Where evaluators agree
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  {signalGroups.aligned.map((dimension) => (
                    <SignalDimensionCard key={dimension.id} dimension={dimension} />
                  ))}
                </div>
              </div>
            )}

            {/* Where they disagree */}
            {signalGroups.conflicted.length > 0 && (
              <div>
                <h3 className="flex items-center gap-2 text-sm font-semibold text-amber-700 mb-3">
                  <ExclamationTriangleIcon className="w-4 h-4" />
                  Where they disagree
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  {signalGroups.conflicted.map((dimension) => (
                    <SignalDimensionCard key={dimension.id} dimension={dimension} />
                  ))}
                </div>
              </div>
            )}

            {/* What's missing */}
            {signalGroups.missing.length > 0 && (
              <div>
                <h3 className="flex items-center gap-2 text-sm font-semibold text-surface-600 mb-3">
                  <QuestionMarkCircleIcon className="w-4 h-4" />
                  What's missing
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  {signalGroups.missing.map((dimension) => (
                    <SignalDimensionCard key={dimension.id} dimension={dimension} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Candidates */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-surface-900">Candidates</h2>
            <span className="text-sm text-surface-500">
              {role.candidates.filter((c) => c.recommendation === 'hire').length} recommended · {' '}
              {role.candidates.filter((c) => c.recommendation === 'undecided').length} undecided · {' '}
              {role.candidates.filter((c) => c.recommendation === 'no-hire').length} not recommended
            </span>
          </div>

          <div className="grid gap-4">
            {sortedCandidates.map((candidate) => (
              <CandidateDecisionCard key={candidate.id} candidate={candidate} />
            ))}
          </div>
        </section>

        {/* Page footer */}
        <footer className="pt-8 border-t border-surface-200">
          <div className="flex items-center justify-center gap-2 text-sm text-surface-500">
            <BoltIcon className="w-4 h-4" />
            <span>All signals are structured, attributable, and auditable. Role created {role.createdAt}.</span>
          </div>
        </footer>
      </main>
    </div>
  )
}

